import { useCallback, useEffect } from 'react';
import { useDevices, useInputSettings } from '@daily-co/daily-react';
import {
  BlurCircular as BlurIcon,
  RadioButtonChecked,
  RadioButtonUnchecked,
  Videocam as CameraIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  selectBackgroundBlurMode,
  selectHideSelfView,
  setBackgroundBlurMode,
  setHideSelfView,
  setSelectedCamera,
} from '../store';
import type { TelehealthSession } from '../types';

interface CameraDropdownProps {
  isRecording: boolean;
  session: TelehealthSession;
  onClose: () => void;
}

const BLUR_OPTIONS = [
  { value: 'none' as const, label: 'None' },
  { value: 'light' as const, label: 'Light blur' },
  { value: 'strong' as const, label: 'Strong blur' },
] as const;

const BLUR_CONFIGS = {
  none: { type: 'none' as const },
  light: { type: 'background-blur' as const, config: { strength: 0.3 } },
  strong: { type: 'background-blur' as const, config: { strength: 0.9 } },
} as const;

export function CameraDropdown({
  isRecording,
  session,
  onClose,
}: CameraDropdownProps) {
  const dispatch = useAppDispatch();
  const { telehealth } = useEventTracking();
  const { cameras, setCamera } = useDevices();
  const { updateInputSettings } = useInputSettings();
  const backgroundBlurMode = useAppSelector(selectBackgroundBlurMode);
  const hideSelfView = useAppSelector(selectHideSelfView);

  // Apply background blur from Redux on mount
  useEffect(() => {
    if (backgroundBlurMode !== 'none') {
      updateInputSettings?.({
        video: { processor: BLUR_CONFIGS[backgroundBlurMode] },
      })?.catch(() => {
        // Blur not supported, reset state
        dispatch(setBackgroundBlurMode('none'));
      });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCameraSelect = useCallback(
    async (deviceId: string) => {
      try {
        await setCamera(deviceId);
        dispatch(setSelectedCamera(deviceId));
        telehealth.deviceSwitched({
          appointment_id: session.appointmentId,
          device_type: 'camera',
          during_recording: isRecording,
        });
        onClose();
      } catch (err) {
        telehealth.deviceSwitchFailed({
          appointment_id: session.appointmentId,
          device_type: 'camera',
          error_code: err instanceof Error ? err.name : 'unknown',
        });
      }
    },
    [setCamera, dispatch, isRecording, session.appointmentId, telehealth, onClose],
  );

  const handleBlurChange = useCallback(
    async (mode: 'none' | 'light' | 'strong') => {
      try {
        await updateInputSettings?.({
          video: { processor: BLUR_CONFIGS[mode] },
        });
        dispatch(setBackgroundBlurMode(mode));
        telehealth.backgroundBlurChanged({
          appointment_id: session.appointmentId,
          mode,
        });
      } catch {
        // Blur not supported — silently fail
      }
    },
    [updateInputSettings, dispatch, session.appointmentId, telehealth],
  );

  const handleSelfViewToggle = useCallback(
    (hidden: boolean) => {
      dispatch(setHideSelfView(hidden));
      telehealth.selfViewToggled({
        appointment_id: session.appointmentId,
        hidden,
      });
    },
    [dispatch, session.appointmentId, telehealth],
  );

  return (
    <Stack sx={{ py: 1 }}>
      {/* Camera section */}
      <Stack direction="row" spacing={1} sx={{alignItems: "center",  px: 2, py: 1 }}>
        <CameraIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2">Camera</Typography>
      </Stack>
      {cameras.map((c) => (
        <ButtonBase
          key={c.device.deviceId}
          onClick={() => handleCameraSelect(c.device.deviceId)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            px: 2,
            py: 0.75,
            gap: 1,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {c.selected ? (
            <RadioButtonChecked sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          <Typography variant="body2" noWrap>
            {c.device.label || `Camera ${c.device.deviceId.slice(0, 8)}`}
          </Typography>
        </ButtonBase>
      ))}

      <Divider sx={{ my: 1 }} />

      {/* Background blur section */}
      <Stack direction="row" spacing={1} sx={{alignItems: "center",  px: 2, py: 1 }}>
        <BlurIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2">Background</Typography>
      </Stack>
      {BLUR_OPTIONS.map((opt) => (
        <ButtonBase
          key={opt.value}
          onClick={() => handleBlurChange(opt.value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            px: 2,
            py: 0.75,
            gap: 1,
            justifyContent: 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {backgroundBlurMode === opt.value ? (
            <RadioButtonChecked sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          <Typography variant="body2">{opt.label}</Typography>
        </ButtonBase>
      ))}

      <Divider sx={{ my: 1 }} />

      {/* Hide self view toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.5,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <HideIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2">Hide self view</Typography>
        </Stack>
        <Switch
          size="small"
          checked={hideSelfView}
          onChange={(_, checked) => handleSelfViewToggle(checked)}
        />
      </Box>
    </Stack>
  );
}
